"use client";

import { useEffect, useEffectEvent, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function usePWA(enableInDevelopment: boolean = false) {
    const [deferredPrompt, setDeferredPrompt] =
        useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    // Initialize installed state with lazy function to avoid effect setState
    const [isInstalled, setIsInstalled] = useState(() => {
        if (typeof window === "undefined") return false;
        const isStandalone = window.matchMedia(
            "(display-mode: standalone)",
        ).matches;
        const isInWebAppiOS =
            (window.navigator as { standalone?: boolean }).standalone === true;
        return isStandalone || isInWebAppiOS;
    });

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = useEffectEvent((e: Event) => {
        e.preventDefault();
        const installEvent = e as BeforeInstallPromptEvent;
        setDeferredPrompt(installEvent);
        setIsInstallable(true);
    });

    // Listen for app installed event
    const handleAppInstalled = useEffectEvent(() => {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
    });

    useEffect(() => {
        // Only register service worker when appropriate
        const isProduction = process.env.NODE_ENV === "production";
        const isHttps =
            window.location.protocol === "https:" ||
            window.location.hostname === "localhost";
        const shouldRegister = isProduction || enableInDevelopment;

        if ("serviceWorker" in navigator && shouldRegister && isHttps) {
            // In development, use minimal service worker; in production, use full SW
            const swPath = isProduction ? "/sw.js" : "/sw-dev.js";

            navigator.serviceWorker
                .register(swPath)
                .then((registration) => {
                    console.log(
                        `${isProduction ? "Production" : "Development"} SW registered:`,
                        registration,
                    );
                })
                .catch((registrationError) => {
                    console.log("SW registration failed:", registrationError);
                });
        }

        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt,
        );
        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener(
                "beforeinstallprompt",
                handleBeforeInstallPrompt,
            );
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, [enableInDevelopment]);

    const installApp = async () => {
        if (!deferredPrompt) return;

        try {
            await deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;

            if (outcome === "accepted") {
                console.log("User accepted the install prompt");
                setIsInstalled(true);
            } else {
                console.log("User dismissed the install prompt");
            }

            setDeferredPrompt(null);
            setIsInstallable(false);
        } catch (error) {
            console.error("Error installing app:", error);
        }
    };

    return {
        isInstallable,
        isInstalled,
        installApp,
    };
}
